import TagLinkCell from '@nile/banyan/src/Pages/ServiceCatalog/TagLinkCell';
import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useImperativeHandle
} from 'react';
import { injectIntl, FormattedMessage, useIntl } from 'react-intl';
import {
  AppContext,
  BackgroundIcon,
  ContentDeciderContainer,
  DeleteModal,
  getTranslatedText,
  isAuthorised,
  Loader,
  SkeletonTableLoading,
  TableContainer,
  Tag,
  toast,
  getDefaultValue,
  updateConditions,
  EventEmitter
} from '@nile/components';
import {
  GET_SERVICE_CATEGORIES,
  AccountClient,
  useQuery,
  GET_WHITELISTED_SERVICE_ITEMS,
  GET_OFFERED_ITEMS_LIST
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { TextCell } from '@nile/mangifera/src/Components/Ticket';
import LinkCell from '@nile/banyan/src/Pages/ServiceCatalog/LinkCell';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import { Button } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import CurrencyCell from '@nile/client_management/src/Pages/Invoice/CurrencyCell';
import InvoiceItemSelectionCell from './InvoiceItemSelectionCell';
import { GET_SERVICE_CATALOG_ITEMS } from '@nile/graphql/src/Query/serviceCatalogNew';
import {
  PriceCell,
  ServiceItemTypeCell
} from '@nile/banyan/src/Pages/ServiceCatalogNew/ProductsAndServicesTable/Cells';

type ListInfoType = {
  totalCount: number;
  pageSize: number;
  page: number;
  condition: any;
};

const ROW_COUNT = 20;

const TableWrapper = styled(TableContainer)`
  .colHeader {
    margin-left: 8px;
  }
`;

const ServiceCatalogTable = React.forwardRef(
  (
    {
      selectedSection,
      checkDisabledRow,
      checkRowHighlight,
      onSelectionQuery,
      accessor,
      itemsToBeExcluded,
      shouldFilterServiceAndProduct = true
    }: any,
    ref
  ) => {
    const intl = useIntl();
    const [localSelectedSection, setLocalSelectedSection] = useState(
      selectedSection
    );
    const selectedSectionRef = React.useRef('');
    selectedSectionRef.current = localSelectedSection;
    const [columnGroup, setColumnGroup] = useState([
      { Header: 'Name', columns: [] }
    ]);
    const [data, setData] = useState([]);

    const [listLoading, setListLoading] = useState(true);
    const [listInfo, setListInfo] = useState<ListInfoType | {}>({});
    const listInfoByRef = useRef<ListInfoType | {}>(null);
    listInfoByRef.current = listInfo;

    const getType = (col) => {
      switch (col.name) {
        case 'selection':
          return 'Check';
        case 'unitPrice':
          return 'Currency';
        default:
          return 'Text';
      }
    };

    const getAccessor = (sItem) => {
      if (sItem?.quantityType === 'HOURS') {
        return sItem?.businessHoursUnitPrice || null;
      }
      return sItem.unitPrice || null;
    };

    function getConditions(categoryName: string, newCondition?: any) {
      const operands = [
        categoryName !== 'all' && {
          attribute: 'category.name',
          operator: 'is',
          value: categoryName
        },
        newCondition,
        shouldFilterServiceAndProduct && {
          joinOperator: 'OR',
          operands: [
            {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'itemType',
                  operator: 'is',
                  value: 'PRODUCT'
                }
              ]
            },
            {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'itemType',
                  operator: 'is',
                  value: 'SERVICE'
                },
                {
                  attribute: 'serviceTypeItem.offeringType',
                  operator: 'is',
                  value: 'QUANTITY'
                }
              ]
            }
          ]
        },
        itemsToBeExcluded?.length && {
          attribute: 'itemId',
          operator: 'notIncludes',
          value: itemsToBeExcluded
        },
        { attribute: 'status', operator: 'is', value: 'ACTIVE' }
      ].filter(Boolean);

      return {
        joinOperator: 'AND',
        operands
      };
    }

    function getListInfo(pageOptions, categoryName) {
      const condition =
        selectedSectionRef.current === 'all'
          ? getConditions(categoryName ?? selectedSectionRef.current)
          : pageOptions?.condition ||
            getConditions(categoryName ?? selectedSectionRef.current);

      return {
        page: pageOptions?.page || 1,
        pageSize: 20,
        condition: condition,
        sort: [
          {
            attribute: 'name',
            order: 'ASC'
          }
        ]
      };
    }

    /**
     * Method to get Service items.
     * Call this method to fetch/refetch the data on mounting/sorting/filtering
     * @param {object} personalization
     * @param {object} pageOptions
     */
    const fetchViewData = async (
      personalization,
      pageOptions,
      isNetworkOnly = false
    ) => {
      setListLoading(true);

      let response;

      const _listInfo = getListInfo(pageOptions);

      if (onSelectionQuery) {
        response = await onSelectionQuery(_listInfo, isNetworkOnly);
      } else {
        response = await AccountClient.query({
          query: GET_SERVICE_CATALOG_ITEMS,
          fetchPolicy: 'no-cache',
          variables: {
            listInfo: _listInfo
          }
        });
      }

      setListLoading(false);
      const cols = [
        {
          name: 'selection',
          label: '  ',
          type: 'Check'
        },
        {
          name: 'name',
          label: getTranslatedText(
            intl,
            'invoiceBuilder.itemSelection.list.name'
          ),
          type: 'Text'
        },
        {
          name: 'description',
          label: getTranslatedText(
            intl,
            'invoiceBuilder.itemSelection.list.description'
          ),
          type: 'Text'
        },
        {
          name: 'itemType',
          label: getTranslatedText(intl, 'common.type'),
          type: 'ServiceItemType'
        },
        {
          name: 'sellingPrice',
          label: getTranslatedText(
            intl,
            'invoiceBuilder.itemSelection.list.unitPrice'
          ),
          type: 'Price'
        },
        {
          name: 'costPrice',
          label: getTranslatedText(
            intl,
            'msp.contract.invoiceBuilder.itemSelection.list.unitCost'
          ),
          type: 'Price'
        }
      ];
      const columns = cols.map((col) => {
        return {
          Header: col.label,
          accessor: col.name,
          width: col.name === 'selection' ? 50 : 200,
          type: col.type,
          id: col.name,
          disableResizing: false,
          className: 'sticky',
          disableSortBy: true,
          editable: false,
          dynamicAccessor: getAccessor,
          disableFilters: true,
          actions: {
            onChange: () => {}
          },
          field: col
          // isBold: col.name === 'unitPrice'
        };
      });
      setColumnGroup([{ Header: 'Name', columns }]);
      if (accessor && response) {
        try {
          let data = accessor.data?.(response) ?? {};
          const listInfo = accessor.listInfo?.(response) ?? {};
          setData(data);
          setListInfo(listInfo);
          return true;
        } catch {
          setData({});
          setListInfo({});
          return false;
        }
      }
      if (response?.data?.getServiceCatalogItems) {
        let items = response.data.getServiceCatalogItems.items;
        setData(items);
        setListInfo({
          ...response.data.getServiceCatalogItems.listInfo,
          condition: pageOptions?.condition || listInfo?.condition || null
        });
        return true;
      }
    };

    const fetchFilteredData = (text) => {
      const condition = getConditions('all', {
        attribute: 'name',
        operator: 'contains',
        value: text
      });
      fetchViewData(null, { page: 1, pageSize: 20, condition: condition });
    };

    useEffect(() => {
      EventEmitter.subscribe('filterData', fetchFilteredData);
      return () => {
        EventEmitter.unsubscribe('filterData');
      };
    }, []);

    useEffect(() => {
      setLocalSelectedSection(selectedSection);
    }, [selectedSection]);

    useImperativeHandle(ref, () => ({
      getListInfo,
      fetchViewData
    }));

    useEffect(() => {
      if (localSelectedSection) {
        fetchViewData();
      }
    }, [localSelectedSection]);

    const defaultColumnCells = {
      Currency: CurrencyCell,
      Check: InvoiceItemSelectionCell,
      Price: PriceCell,
      ServiceItemType: ServiceItemTypeCell
    };

    const setSelectedItems = (items) => {};

    const context = {
      setRecords: setSelectedItems,
      viewConditions: listInfoByRef?.current?.condition || null
    };

    return (
      <TableWrapper>
        <ContentDeciderContainer
          isLoading={!columnGroup[0]?.columns?.length}
          hasContent
          fullHeight
          mainContent={
            <TableView
              noIndicators
              noBulkSelect
              context={context}
              columns={columnGroup}
              data={data || []}
              setData={setData}
              rowCount={ROW_COUNT}
              refreshTable={fetchViewData}
              listInfo={listInfo}
              defaultColumnCells={defaultColumnCells}
              pagination={
                Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1
              }
              pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
              recordCount={listInfo.totalCount}
              activePage={listInfo.page || 0}
              isLoading={listLoading}
              fullHeight
              checkDisabledRow={checkDisabledRow}
              checkRowHighlight={checkRowHighlight}
            />
          }
          loadingContent={<SkeletonTableLoading />}
          notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
        />
      </TableWrapper>
    );
  }
);

export default ServiceCatalogTable;
